import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { useLocation, useParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Home } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { stripLangPrefix } from "@/hooks/useLocalePath";
import { getGenerator } from "@/lib/seo/schemaGenerators";

interface BreadcrumbEntry {
  label: string;
  href?: string;
}

export const AppBreadcrumb = () => {
  const location = useLocation();
  const params = useParams();
  const { language } = useLanguage();

  const tx = (fr: string, en: string, es: string) =>
    language === "fr" ? fr : language === "es" ? es : en;

  const getBreadcrumbs = (): BreadcrumbEntry[] => {
    // Strip language prefix for route matching
    const path = stripLangPrefix(location.pathname);
    const crumbs: BreadcrumbEntry[] = [];

    if (path === "/") return [];

    if (path === "/pricing") {
      crumbs.push({ label: tx("Tarifs", "Pricing", "Precios") });
    } else if (path === "/docs") {
      crumbs.push({ label: tx("Documentation API", "API Documentation", "Documentación API") });
    } else if (path === "/auth") {
      crumbs.push({ label: tx("Connexion", "Login", "Iniciar sesión") });
    } else if (path === "/account") {
      crumbs.push({ label: tx("Mon compte", "My Account", "Mi cuenta") });
    } else if (path === "/terms") {
      crumbs.push({ label: tx("CGV", "Terms", "Términos") });
    } else if (path === "/privacy") {
      crumbs.push({ label: tx("Confidentialité", "Privacy", "Privacidad") });
    } else if (path === "/contact") {
      crumbs.push({ label: tx("Nous Joindre", "Contact Us", "Contáctenos") });
    } else if (path === "/admin") {
      crumbs.push({ label: "Admin" });
    } else if (path === "/structured-data") {
      crumbs.push({ label: tx("Données structurées", "Structured data", "Datos estructurados") });
    } else if (path.startsWith("/structured-data/")) {
      crumbs.push({ label: tx("Données structurées", "Structured data", "Datos estructurados"), href: "/structured-data" });
      crumbs.push({ label: getGenerator(path.split("/")[2])?.type || "" });
    } else if (path.startsWith("/projects/")) {
      crumbs.push({ label: tx("Projets", "Projects", "Proyectos"), href: "/account?tab=projects" });
      crumbs.push({ label: params.slug || tx("Détail", "Detail", "Detalle") });
    } else if (path.startsWith("/shared/")) {
      crumbs.push({ label: tx("Projet partagé", "Shared Project", "Proyecto compartido") });
    }

    return crumbs;
  };

  const crumbs = getBreadcrumbs();

  if (crumbs.length === 0) return null;

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/" className="flex items-center gap-1">
              <Home className="w-3.5 h-3.5" />
              <span className="sr-only sm:not-sr-only">{tx("Accueil", "Home", "Inicio")}</span>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => (
          <span key={index} className="contents">
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {index < crumbs.length - 1 && crumb.href ? (
                <BreadcrumbLink asChild>
                  <Link to={crumb.href}>{crumb.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </span>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
